/**
 * Frontend Cache & Performance Configuration
 * Optimized for heavy traffic handling
 */

export const CACHE_CONFIG = {
  // API Cache Duration (in milliseconds)
  API: {
    EVENTS: 5 * 60 * 1000,           // 5 minutes
    USER_PROFILE: 10 * 60 * 1000,     // 10 minutes
    ORGANIZATIONS: 15 * 60 * 1000,    // 15 minutes
    STATIC_DATA: 60 * 60 * 1000,      // 1 hour
  },

  // Browser Cache Headers
  BROWSER: {
    IMMUTABLE: 'public, max-age=31536000, immutable',
    LONG_TERM: 'public, max-age=604800',           // 1 week
    MEDIUM_TERM: 'public, max-age=3600',           // 1 hour
    SHORT_TERM: 'public, max-age=300',             // 5 minutes
    NO_CACHE: 'public, max-age=0, must-revalidate',
  },

  // Session Configuration
  SESSION: {
    TIMEOUT: 30 * 60 * 1000,           // 30 minutes
    ACTIVITY_TIMEOUT: 5 * 60 * 1000,   // 5 minutes
    REMEMBER_ME_DURATION: 7 * 24 * 60 * 60 * 1000, // 7 days
  },

  // Rate Limiting
  RATE_LIMIT: {
    API_REQUESTS_PER_MINUTE: 60,
    LOGIN_ATTEMPTS_PER_MINUTE: 15,
    API_REQUESTS_PER_HOUR: 1000,
  },

  // Concurrent Connection Handling
  CONCURRENT: {
    MAX_SIMULTANEOUS_REQUESTS: 10,
    REQUEST_TIMEOUT: 30 * 1000,        // 30 seconds
    RETRY_ATTEMPTS: 3,
    RETRY_DELAY: 1000,                 // 1 second
  },
};

export const PERFORMANCE_CONFIG = {
  // Image Optimization
  IMAGE: {
    LAZY_LOAD: true,
    SIZES: {
      THUMBNAIL: '100px',
      SMALL: '400px',
      MEDIUM: '800px',
      LARGE: '1200px',
    },
  },

  // Font Loading
  FONT: {
    STRATEGY: 'swap', // swap | fallback | optional
    PRELOAD: ['Inter', 'Plus Jakarta Sans'],
  },

  // Code Splitting
  CODE_SPLIT: {
    ENABLED: true,
    DYNAMIC_IMPORTS: true,
  },

  // Prefetch Strategy
  PREFETCH: {
    ENABLED: true,
    ROUTES: ['/features', '/pricing', '/about'],
    CRITICAL_RESOURCES: true,
  },
};

export const MONITORING_CONFIG = {
  // Performance Monitoring
  METRICS: {
    ENABLED: true,
    TRACK_CLS: true,    // Cumulative Layout Shift
    TRACK_FID: true,    // First Input Delay
    TRACK_LCP: true,    // Largest Contentful Paint
    TRACK_FCP: true,    // First Contentful Paint
  },

  // Error Tracking
  ERROR: {
    ENABLED: true,
    SENTRY: true,
    LOG_LEVEL: 'error',
  },

  // Analytics
  ANALYTICS: {
    ENABLED: true,
    TRACK_PAGEVIEWS: true,
    TRACK_EVENTS: true,
    SAMPLE_RATE: 0.1, // 10% of users for heavy traffic
  },
};

/**
 * Heavy Traffic Optimization Strategies:
 * 
 * 1. Connection Pooling
 *    - Database: Min 2, Max 20 connections
 *    - API: HTTP/2 connection reuse
 *    - Redis: Connection pooling enabled
 * 
 * 2. Caching Strategy
 *    - Server-side: Cache API responses (5-60 minutes)
 *    - Client-side: Leverage browser cache headers
 *    - CDN: Static assets cached for 1 week minimum
 *    - Session: Store in Redis for horizontal scaling
 * 
 * 3. Rate Limiting
 *    - Login attempts: 15 per minute per email+IP
 *    - API requests: 60 per minute per user
 *    - Global IP limit: 100 requests per minute
 * 
 * 4. Concurrent Login Handling
 *    - Use database row locking during login
 *    - Track login sessions with timestamps
 *    - Earlier timestamps get priority in ID assignment
 *    - Store login sessions in database for audit trail
 * 
 * 5. Load Balancing
 *    - Use sticky sessions for concurrent requests
 *    - Distribute requests across multiple app instances
 *    - Queue long-running tasks (emails, reports, etc.)
 * 
 * 6. Database Optimization
 *    - Indexes on frequently queried columns
 *    - Composite indexes for common query patterns
 *    - Connection pooling with timeouts
 *    - Query optimization and caching
 * 
 * 7. Frontend Optimization
 *    - Code splitting and lazy loading
 *    - Image optimization and responsive sizing
 *    - CSS and JS minification
 *    - Service Worker for offline support
 */
